import React, { createContext, useContext, useState } from "react";
import { gaEvent } from "../../utils/gaAnalytics";

const DownloadContext = createContext(null);

export const useDownload = () => useContext(DownloadContext);

const formatMB = (bytes) => (bytes / (1024 * 1024)).toFixed(1);

export const DownloadProvider = ({ children }) => {
  const [progress, setProgress] = useState(0);
  const [downloadedBytes, setDownloadedBytes] = useState(0);
  const [totalBytes, setTotalBytes] = useState(0);
  const [isDownloading, setIsDownloading] = useState(false);
  const [isComplete, setIsComplete] = useState(false);
  const [error, setError] = useState(null);

  const DEFAULT_URL =
    process.env.REACT_APP_INSTALLER_URL ||
    "/ztaupdate/ZeroTypingAI.msi";

  const startDownload = async (
    url = DEFAULT_URL,
    filename = "ZeroTypingAI.msi"
  ) => {
    if (isDownloading) return;
    setIsDownloading(true);
    setIsComplete(false);
    setError(null);
    setProgress(0);
    setDownloadedBytes(0);
    setTotalBytes(0);

    // eslint-disable-next-line no-console
    console.info("[ZT download] starting", url);

    try {
      const response = await fetch(url, { mode: "cors", cache: "no-store" });
      if (!response.ok) {
        throw new Error(`HTTP ${response.status} ${response.statusText}`);
      }
      if (!response.body) {
        throw new Error("Response has no body (browser cannot stream)");
      }

      const total = Number(response.headers.get("Content-Length") || 0);
      setTotalBytes(total);
      if (!total) {
        // eslint-disable-next-line no-console
        console.warn(
          "[ZT download] server did not send Content-Length — progress % will not be accurate"
        );
      }

      const reader = response.body.getReader();
      const chunks = [];
      let received = 0;

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        chunks.push(value);
        received += value.length;
        setDownloadedBytes(received);
        if (total) {
          setProgress((received / total) * 100);
        }
      }

      const blob = new Blob(chunks);
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(blobUrl);

      setProgress(100);
      setIsComplete(true);
      gaEvent("file_download", {
        platform: "windows",
        file_name: filename,
        file_extension: "msi",
        link_url: url,
        total_bytes: total || undefined,
      });
      // eslint-disable-next-line no-console
      console.info("[ZT download] complete");
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error(
        "[ZT download] stream failed — falling back to direct download. Reason:",
        err
      );
      setError(err);
      gaEvent("file_download_failed", {
        platform: "windows",
        file_name: filename,
        link_url: url,
        error_message: String(err?.message || err),
      });
      // Fallback: let the browser handle it directly (no progress tracking)
      const a = document.createElement("a");
      a.href = url;
      a.target = "_blank";
      a.rel = "noopener noreferrer";
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <DownloadContext.Provider
      value={{
        progress,
        downloadedBytes,
        totalBytes,
        downloadedMB: formatMB(downloadedBytes),
        totalMB: totalBytes ? formatMB(totalBytes) : null,
        isDownloading,
        isComplete,
        error,
        startDownload,
      }}
    >
      {children}
    </DownloadContext.Provider>
  );
};
